// Pure helpers for the admin user form (create + edit): validation and patch building.

const PHONE_PATTERN = /^\+[0-9]{6,15}$/;
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Accounts a child account (employee) can be created under: active, not employees themselves.
export const buildOwnerOptions = (rows) =>
  (rows || [])
    .filter((row) => row && !row.ownerUserId && row.isActive !== false)
    .sort((left, right) => String(left.username).localeCompare(String(right.username), 'es'))
    .map((row) => ({
      key: row.username,
      value: row.username,
      text: `${row.username}${row.email ? ` — ${row.email}` : ''}${row.role === 'ADMIN' ? ' (admin)' : ''}`,
    }));

export const marketplaceCountryOptions = [
  { key: 'AR', text: 'Argentina', value: 'AR' },
  { key: 'BO', text: 'Bolivia', value: 'BO' },
  { key: 'BR', text: 'Brasil', value: 'BR' },
  { key: 'CL', text: 'Chile', value: 'CL' },
  { key: 'CO', text: 'Colombia', value: 'CO' },
  { key: 'CR', text: 'Costa Rica', value: 'CR' },
  { key: 'DO', text: 'República Dominicana', value: 'DO' },
  { key: 'EC', text: 'Ecuador', value: 'EC' },
  { key: 'GT', text: 'Guatemala', value: 'GT' },
  { key: 'HN', text: 'Honduras', value: 'HN' },
  { key: 'MX', text: 'México', value: 'MX' },
  { key: 'NI', text: 'Nicaragua', value: 'NI' },
  { key: 'PA', text: 'Panamá', value: 'PA' },
  { key: 'PE', text: 'Perú', value: 'PE' },
  { key: 'PY', text: 'Paraguay', value: 'PY' },
  { key: 'SV', text: 'El Salvador', value: 'SV' },
  { key: 'UY', text: 'Uruguay', value: 'UY' },
  { key: 'VE', text: 'Venezuela', value: 'VE' },
];

// Store a single searchable/callable representation while letting admins paste
// common human formats such as "+54 9 11 6793-4361".
export const normalizePhoneNumber = (value) => {
  const compact = String(value || '').trim().replace(/[\s().-]/g, '');
  if (!compact || !/^\+?\d+$/.test(compact)) return compact;
  return `+${compact.replace(/^\+/, '')}`;
};

export const validateBillingFields = ({ phoneNumber = '', nextPaymentAmount = '', nextPaymentDueDate = '' }) => {
  const phone = normalizePhoneNumber(phoneNumber);
  if (phone && !PHONE_PATTERN.test(phone)) {
    return 'Ingresá un teléfono internacional válido, entre 6 y 15 dígitos.';
  }
  const amount = String(nextPaymentAmount).trim();
  if (amount) {
    const value = Number(amount);
    if (!Number.isFinite(value) || value < 0) {
      return 'Ingresá un monto válido mayor o igual a 0.';
    }
  }
  const dueDate = String(nextPaymentDueDate).trim();
  if (dueDate && !DATE_PATTERN.test(dueDate)) {
    return 'La fecha de vencimiento debe tener el formato AAAA-MM-DD.';
  }
  return null;
};

export const validateEmail = (email) =>
  EMAIL_PATTERN.test(String(email || '').trim()) ? null : 'Ingresá un email válido.';

// Optional billing fields of the "create user" payload: only filled fields are sent.
export const buildCreateBillingPayload = ({ phoneNumber, nextPaymentAmount, nextPaymentDueDate }) => {
  const payload = {};
  if (String(phoneNumber || '').trim()) payload.phoneNumber = normalizePhoneNumber(phoneNumber);
  if (String(nextPaymentAmount ?? '').trim()) payload.nextPaymentAmount = Number(nextPaymentAmount);
  if (String(nextPaymentDueDate || '').trim()) payload.nextPaymentDueDate = String(nextPaymentDueDate).trim();
  return payload;
};

const toForm = (row) => ({
  email: row.email || '',
  role: row.role || 'USER',
  isActive: row.isActive !== false,
  marketplaceCountry: row.marketplaceCountry || 'AR',
  phoneNumber: row.phoneNumber || '',
  nextPaymentAmount:
    row.nextPaymentAmount !== null && row.nextPaymentAmount !== undefined
      ? String(row.nextPaymentAmount)
      : '',
  nextPaymentDueDate: row.nextPaymentDueDate || '',
});

export const buildInitialForm = toForm;

// Returns only what changed, split by the endpoint that persists it.
export const buildEditPatches = (row, form) => {
  const original = toForm(row);
  const profile = {};
  if (form.email.trim() !== original.email) profile.email = form.email.trim();
  if (form.role !== original.role) profile.role = form.role;
  if (form.isActive !== original.isActive) profile.isActive = form.isActive;
  if (form.marketplaceCountry !== original.marketplaceCountry) {
    profile.marketplaceCountry = form.marketplaceCountry;
  }

  const billing = {};
  if (form.phoneNumber.trim() !== original.phoneNumber) {
    billing.phoneNumber = form.phoneNumber.trim() === '' ? null : normalizePhoneNumber(form.phoneNumber);
  }
  if (String(form.nextPaymentAmount).trim() !== original.nextPaymentAmount) {
    billing.nextPaymentAmount =
      String(form.nextPaymentAmount).trim() === '' ? null : Number(form.nextPaymentAmount);
  }
  if (form.nextPaymentDueDate.trim() !== original.nextPaymentDueDate) {
    billing.nextPaymentDueDate = form.nextPaymentDueDate.trim() === '' ? null : form.nextPaymentDueDate.trim();
  }
  return { profile, billing };
};
